import { useCallback } from 'react';
import useAjax from '@apis/useAjax';
import {
    GET_CLUB_STOCK_OUT_OPTIONS_API,
    POST_CLUB_STOCK_OUT_OVERVIEW_API,
    GET_CLUB_STOCK_OUT_API,
    GET_CLUB_STOCK_OUT_RECORD_API,
    POST_CLUB_STOCK_OUT_TRANSFER_API,
    POST_CLUB_STOCK_OUT_RETURN_API,
    GET_CLUB_STOCK_OUT_DEFECTIVE_ITEMS_API,
    POST_CLUB_STOCK_OUT_CONSUMPTION_API,
    POST_CLUB_STOCK_OUT_DEFECTIVE_CONSUMPTION_API,
    POST_CLUB_STOCK_OUT_GIFT_CONSUMPTION_API,
} from '@apis/apiPath';

/**
 * 店鋪出貨相關 API Hook
 * 對應後端 ClubStockOutController
 */
const useClubStockOutApi = () => {
    const Ajax = useAjax();

    /**
     * 出貨單下拉式選單
     * @param {boolean} [skipAll=false]
     */
    const getClubStockOutOptionsApi = useCallback(
        async (skipAll = false) => await Ajax.get(GET_CLUB_STOCK_OUT_OPTIONS_API(skipAll)),
        // eslint-disable-next-line
        [],
    );

    /**
     * 取得出貨單總覽(分頁列表)
     * @param {object} params ClubStockOutPaginationRequestModel (pageIndex, pageSize, ...)
     * @returns {Promise<{ list, pageIndex, pageSize, totalCount, totalPage }>}
     */
    const postClubStockOutOverviewApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_OVERVIEW_API, params),
        // eslint-disable-next-line
        [],
    );

    /**
     * 取得出貨單明細
     * @param {number} stockOutID 出貨單代碼
     */
    const getClubStockOutApi = useCallback(
        async (stockOutID) => await Ajax.get(GET_CLUB_STOCK_OUT_API(stockOutID)),
        // eslint-disable-next-line
        [],
    );

    /**
     * 取得出貨單記錄
     * @param {number} stockOutID 出貨單代碼
     * @returns {Promise<Array<{ title: string, pdfUrl: string, employeeName: string, createdDate: string }>>}
     */
    const getClubStockOutRecordApi = useCallback(
        async (stockOutID) => await Ajax.get(GET_CLUB_STOCK_OUT_RECORD_API(stockOutID)),
        // eslint-disable-next-line
        [],
    );

    /**
     * 建立店鋪調撥(出貨)單
     * @param {object} params ClubStockOutRequestModel
     */
    const postClubStockOutTransferApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_TRANSFER_API, params),
        // eslint-disable-next-line
        [],
    );

    /**
     * 建立瑕疵退貨單
     * @param {object} params ClubStockOutReturnRequestModel
     */
    const postClubStockOutReturnApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_RETURN_API, params),
        // eslint-disable-next-line
        [],
    );

    /**
     * 取得店鋪(所有未處理)瑕疵清單
     */
    const getClubStockOutDefectiveItemsApi = useCallback(
        async () => await Ajax.get(GET_CLUB_STOCK_OUT_DEFECTIVE_ITEMS_API),
        // eslint-disable-next-line
        [],
    );

    /**
     * 新增商品領用單
     * @param {object} params ClubStockOutConsumptionRequestModel
     */
    const postClubStockOutConsumptionApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_CONSUMPTION_API, params),
        // eslint-disable-next-line
        [],
    );

    /**
     * 建立瑕疵領用單
     * @param {object} params ClubDefectiveConsumptionRequestModel
     */
    const postClubStockOutDefectiveConsumptionApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_DEFECTIVE_CONSUMPTION_API, params),
        // eslint-disable-next-line
        [],
    );

    /**
     * 新增贈品領用單（從一般倉扣庫，轉入贈品倉）
     * @param {object} params ClubStockOutGiftConsumptionRequestModel
     */
    const postClubStockOutGiftConsumptionApi = useCallback(
        async (params) => await Ajax.post(POST_CLUB_STOCK_OUT_GIFT_CONSUMPTION_API, params),
        // eslint-disable-next-line
        [],
    );

    return {
        getClubStockOutOptionsApi,
        postClubStockOutOverviewApi,
        getClubStockOutApi,
        getClubStockOutRecordApi,
        postClubStockOutTransferApi,
        postClubStockOutReturnApi,
        getClubStockOutDefectiveItemsApi,
        postClubStockOutConsumptionApi,
        postClubStockOutDefectiveConsumptionApi,
        postClubStockOutGiftConsumptionApi,
    };
};

export default useClubStockOutApi;
